/*
 **	Unit Conversion Utilities
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

export type PressureUnit = 'inWC' | 'Pa'

// Conversion constants
// 1 inch of water column (inWC) = 249.08890833333 Pascal
const INWC_TO_PASCAL = 249.09
const PASCAL_TO_INWC = 1 / INWC_TO_PASCAL // ~0.00401463

/**
 * Convert pressure between inWC and Pascal
 * @param value - The pressure value to convert
 * @param from - The source unit
 * @param to - The target unit
 * @returns The converted pressure value
 */
export function convertPressure(
  value: number,
  from: PressureUnit,
  to: PressureUnit
): number {
  if (from === to) return value

  if (from === 'inWC' && to === 'Pa') {
    return value * INWC_TO_PASCAL
  }

  if (from === 'Pa' && to === 'inWC') {
    return value * PASCAL_TO_INWC
  }

  return value
}

/**
 * Format pressure value for display with appropriate decimal places
 * @param value - The pressure value in inWC (source unit)
 * @param unit - The unit to display in
 * @returns Formatted string with the value and unit
 */
export function formatPressure(value: number, unit: PressureUnit): string {
  const convertedValue = convertPressure(value, 'inWC', unit)

  // inWC uses 4 decimal places, Pascal uses 2 decimal places
  const decimals = unit === 'inWC' ? 4 : 2

  return `${convertedValue.toFixed(decimals)} ${unit}`
}

/**
 * Format pressure value without unit label
 * @param value - The pressure value in inWC (source unit)
 * @param unit - The unit to display in
 * @returns Formatted string with just the value
 */
export function formatPressureValue(value: number, unit: PressureUnit): string {
  const convertedValue = convertPressure(value, 'inWC', unit)

  // inWC uses 4 decimal places, Pascal uses 2 decimal places
  const decimals = unit === 'inWC' ? 4 : 2

  return convertedValue.toFixed(decimals)
}

/**
 * Get the appropriate input step value for a given unit
 * @param unit - The pressure unit
 * @returns The step value for number inputs
 */
export function getPressureInputStep(unit: PressureUnit): number {
  return unit === 'inWC' ? 0.01 : 2.5
}
